import { Box, Grid, Typography } from "@mui/material";
import React, { createContext, useEffect, useRef, useState } from "react";
import { newWebSocket } from "../../../api";
import { useAppIdMap } from "../../../hook/app";
import {
  useFetchLogStreamListener,
  useLogStream,
  useLogStreamContext,
  useLogStreamNameEditor,
} from "../../../hook/page/block/logStream";
import { LogStreamContextValue } from "../../../types/logStream";
import { LogStreamBlock } from "../../../types/notebook/note/block/logStream";
import { CodeEditorCodeLogs, CodeEditorProvider } from "../../editor";
import { NoteBlockContainer } from "../../note";
import { CopyClipboard } from "../../copyClipboard";
import { DynamicTextField } from "../../dynamicTextField";
import { StaticHtmlBlock } from "../../staticHtmlBlock";

export const LogStreamContext = createContext<
  LogStreamContextValue | undefined
>(undefined);

export const LogStreamProvider: React.FC<{
  children: React.ReactNode;
  logStreamId: string;
}> = ({ logStreamId, children }) => {
  const { logStream } = useLogStream(logStreamId);
  const { appId } = useAppIdMap();
  const location = `omega/query-omega/apps/${appId}/log-stream/streams/${logStreamId}`;
  const wsRef = useRef<WebSocket | null>(null);
  const heartBeatIntervalRef = useRef<number | null>(null);
  const [messages, setMessages] = useState<[string, string][]>([]);
  const addMessage = (message: [string, string]) =>
    setMessages((messages) => [...messages, message]);
  const messagesString = messages
    .map(([date, data]) => `${date}\t${JSON.stringify(data)}`)
    .join("\n");

  useEffect(() => {
    if (!appId) return;
    if (!logStreamId) return;
    if (wsRef.current !== null) return;
    const ws = newWebSocket({
      path: "log_stream",
      queryParams: {
        limit: 100,
        location,
        "within-period": "P1Y",
      },
    });
    ws.onopen = (data) => {
      heartBeatIntervalRef.current = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send("heartbeat");
        }
      }, 1000);
    };
    ws.onmessage = ({ data }) => {
      const message = JSON.parse(data);
      addMessage(message);
    };
    wsRef.current = ws;
    return () => {
      const heartbeatInterval = heartBeatIntervalRef.current;
      if (heartbeatInterval !== null) {
        clearInterval(heartbeatInterval);
      }
      const ws = wsRef.current;
      if (ws) {
        if (ws.readyState === WebSocket.CONNECTING) {
          ws.onopen = () => ws.close();
          ws.onmessage = null;
        } else if (ws.readyState === WebSocket.OPEN) {
          ws.close();
        }
        wsRef.current = null;
      }
    };
  }, [logStreamId, appId]);

  const contextValue: LogStreamContextValue = {
    logStream,
    location,
    messages: messagesString,
  };
  return (
    <LogStreamContext.Provider value={contextValue}>
      {children}
    </LogStreamContext.Provider>
  );
};

export const LogStreamBlockComponentInner: React.FC<{
  logStreamId: string | undefined;
}> = ({ logStreamId }) => {
  const { messages, location, logStream } = useLogStreamContext();
  const { name, setName } = useLogStreamNameEditor(logStreamId);
  return (
    <Box data-testid="log-stream-block">
      <NoteBlockContainer name="Log Stream">
        <Grid container spacing={1}>
          <Grid item xs={2}>
            <Typography>Location</Typography>
          </Grid>
          <Grid item xs={10}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Typography data-testid="log-stream-location">
                {location}
              </Typography>
              <CopyClipboard value={location ?? ""} />
            </Box>
          </Grid>
          <Grid item xs={2}>
            <Typography>Id</Typography>
          </Grid>
          <Grid item xs={10}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Typography data-testid="log-stream-id">
                {logStream?.["log-stream-id"]}
              </Typography>
              <CopyClipboard value={logStream?.["log-stream-id"] ?? ""} />
            </Box>
          </Grid>
          <Grid item xs={2}>
            <Typography>Name</Typography>
          </Grid>
          <Grid item xs={10}>
            <DynamicTextField text={name} setText={setName} />
          </Grid>
          {!messages.trim() && (
            <StaticHtmlBlock html="<p>No log messages yet. Logs appear here as your components emit events.</p>" />
          )}
          <Grid item xs={12} data-testid="log-stream-output">
            <CodeEditorProvider logs={messages}>
              <CodeEditorCodeLogs />
            </CodeEditorProvider>
          </Grid>
        </Grid>
      </NoteBlockContainer>
    </Box>
  );
};

export const LogStreamBlockComponent: React.FC<{ block: LogStreamBlock }> = React.memo(({
  block,
}) => {
  const { "log-stream-id": logStreamId } = block;
  useFetchLogStreamListener(logStreamId);
  return (
    <LogStreamProvider logStreamId={logStreamId}>
      <LogStreamBlockComponentInner logStreamId={logStreamId} />
    </LogStreamProvider>
  );
});
